import { useState } from 'react';
import type { Harvest } from '../../../../types';
import { HARVEST_STATUS, HARVEST_FILTER_OPTIONS } from '../../../../utils/status';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';

interface HarvestFiltersProps {
  harvests: Harvest[];
  farmers: { id: string; name: string }[];
  seasons: { id: string; name: string }[];
  onFilterChange: (filtered: Harvest[]) => void;
}

export default function HarvestFilters({ harvests, farmers, seasons, onFilterChange }: HarvestFiltersProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [farmerFilter, setFarmerFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [lowYield, setLowYield] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const statusOptions: string[] = Object.values(HARVEST_STATUS);

  const applyFilter = (
    s: string,
    st: string,
    f: string,
    se: string,
    ly: boolean,
    pc: boolean,
  ) => {
    const filtered = harvests.filter((h) => {
      const sl = s.toLowerCase();
      const farmerName = farmers.find((fa) => fa.id === h.farmerId)?.name.toLowerCase() || '';
      const seasonName = seasons.find((seas) => seas.id === h.seasonId)?.name.toLowerCase() || '';
      const matchSearch = !sl
        || farmerName.includes(sl)
        || seasonName.includes(sl)
        || h.loaiSanPham.toLowerCase().includes(sl)
        || h.cayTrong.toLowerCase().includes(sl)
        || h.maLanThuHoach.toLowerCase().includes(sl);
      const matchStatus = !st || h.status === st;
      const matchFarmer = !f || h.farmerId === f;
      const matchSeason = !se || h.seasonId === se;
      const matchLow = !ly || h.status === 'Sản lượng thấp';
      const matchPending = !pc || (h.status === 'Nông hộ đã cập nhật' || h.status === 'Chờ HTX xác nhận');
      return matchSearch && matchStatus && matchFarmer && matchSeason && matchLow && matchPending;
    });
    onFilterChange(filtered);
  };

  const update = (key: string, value: unknown) => {
    let nextSearch = search;
    let nextStatus = statusFilter;
    let nextFarmer = farmerFilter;
    let nextSeason = seasonFilter;
    let nextLow = lowYield;
    let nextPending = pendingConfirm;

    if (key === 'search') { nextSearch = value as string; setSearch(nextSearch); }
    if (key === 'status') { nextStatus = value as string; setStatusFilter(nextStatus); }
    if (key === 'farmer') { nextFarmer = value as string; setFarmerFilter(nextFarmer); }
    if (key === 'season') { nextSeason = value as string; setSeasonFilter(nextSeason); }
    if (key === 'lowYield') { nextLow = value as boolean; setLowYield(nextLow); }
    if (key === 'pending') { nextPending = value as boolean; setPendingConfirm(nextPending); }

    applyFilter(nextSearch, nextStatus, nextFarmer, nextSeason, nextLow, nextPending);
  };

  return (
    <SearchFilterBar value={search} onChange={(value) => update('search', value)} placeholder="Tìm nông hộ, mùa vụ, cây trồng..." selected={statusFilter} onSelect={(value) => update('status', value)} options={[{ value: '', label: 'Tất cả trạng thái', count: harvests.length }, ...statusOptions.map((status) => ({ value: status, label: status }))]}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select
          value={farmerFilter}
          onChange={(e) => update('farmer', e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
        >
          <option value="">Tất cả nông hộ</option>
          {farmers.map((fa) => <option key={fa.id} value={fa.id}>{fa.name}</option>)}
        </select>
        <select
          value={seasonFilter}
          onChange={(e) => update('season', e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
        >
          <option value="">Tất cả mùa vụ</option>
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 cursor-pointer text-sm">
          <input type="checkbox" checked={lowYield} onChange={(e) => update('lowYield', e.target.checked)} className="accent-agrida-700" />
          <span className="text-slate-700">Sản lượng thấp</span>
        </label>
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 cursor-pointer text-sm">
          <input type="checkbox" checked={pendingConfirm} onChange={(e) => update('pending', e.target.checked)} className="accent-agrida-700" />
          <span className="text-slate-700">Chờ xác nhận</span>
        </label>
      </div>
    </SearchFilterBar>
  );
}
